<template>
  <div class="card-header">
    <div class="d-flex justify-content-between align-items-center">
      <h5 class="mb-0">
        Limits
        <material-badge
          v-if="accountTier === 'PAID'"
          variant="fill"
          color="warning"
          size="sm"
          style="border-radius: 0%;"
        >Personal Tier &nbsp;<FontAwesomeIcon icon="fa-solid fa-crown" style="font-size: 13px;" />
        </material-badge>
        <material-badge
          v-if="accountTier === 'FREE'"
          variant="fill"
          color="warning"
          size="sm"
          style="border-radius: 0%;"
        >Free Tier
        </material-badge>
      </h5>
      <material-button
        v-if="accountTier === 'FREE'"
        color="shareguard"
        variant="outline"
        style="border-color: white"
        size="sm"
        @click="handleUpgradeAccount"
        >
        <FontAwesomeIcon icon="fa-solid fa-arrow-up-right-from-square" style="font-size: 16px;" />
      </material-button>
    </div>
  </div>
  <div v-if="isLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
    <span class="spinner-border text-primary" role="status"></span>
    <span class="ms-2">Loading your subscription details...</span>
  </div>
  <div v-else class="card-body pt-0">
    <div class="mb-3">
      <span v-if="stats && stats.subscription && stats.subscription.limits && stats.subscription.limits.currentPeriodEnd && accountTier === 'PAID'" class="ms-auto">
        <p class="mb-0">Next billing date: {{ new Date(stats.subscription.limits.currentPeriodEnd).toLocaleDateString() }}</p>  
      </span>
    </div>
    <!-- Storage Usage -->
    <div class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <span>Storage</span>
        <span class="text-muted small">{{ storage.usedFormatted }} of {{ storage.limitFormatted }} used ({{ storage.usedPercentage }}%)</span>
      </div>
      <div class="progress" style="height: 8px;">
        <div class="progress-bar bg-info" role="progressbar" :style="{ width: storage.usedPercentage + '%' }" :aria-valuenow="storage.usedPercentage" aria-valuemin="0" :aria-valuemax="100"></div>
      </div>
    </div>
    <!-- Uploads -->
    <!-- <div class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <span>Uploads</span>
        <span class="text-muted small">{{ transfers.uploads.count }} of {{ transfers.uploads.limit }} this month</span>
      </div>
      <div class="progress" style="height: 8px;">
        <div class="progress-bar bg-primary" role="progressbar" :style="{ width: transfers.uploads.percentage + '%' }" :aria-valuenow="transfers.uploads.percentage" aria-valuemin="0" :aria-valuemax="100"></div>
      </div>
    </div> -->
    <!-- Downloads -->
    <!-- <div class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <span>Downloads</span>
        <span class="text-muted small">{{ transfers.downloads.count }} of {{ transfers.downloads.limit }} this month</span>
      </div>
      <div class="progress" style="height: 8px;">
        <div class="progress-bar bg-success" role="progressbar" :style="{ width: transfers.downloads.percentage + '%' }" :aria-valuenow="transfers.downloads.percentage" aria-valuemin="0" :aria-valuemax="100"></div>
      </div>
    </div> -->
    <!-- Contacts -->
    <div class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <span>Contacts</span>
        <span class="text-muted small">{{ contacts.count }} of {{ contacts.limit }} used</span>
      </div>
      <div class="progress" style="height: 8px;">
        <div class="progress-bar" :class="contacts.percentage >= 100 ? 'bg-danger' : 'bg-warning'" role="progressbar" :style="{ width: contacts.percentage + '%' }" :aria-valuenow="contacts.percentage" aria-valuemin="0" :aria-valuemax="100"></div>
      </div>
      <div v-if="contacts.percentage >= 100" class="text-danger small mt-1">You have reached your contact limit.</div>
    </div>
    <!-- Shares -->
    <div class="mb-3">
      <div class="d-flex justify-content-between align-items-center">
        <span>Shares</span>
        <span class="text-muted small">{{ sharing.count }} of {{ sharing.limit }} used</span>
      </div>
      <div class="progress" style="height: 8px;">
        <div class="progress-bar bg-secondary" role="progressbar" :style="{ width: sharing.percentage + '%' }" :aria-valuenow="sharing.percentage" aria-valuemin="0" :aria-valuemax="100"></div>
      </div>
    </div>
    <div class="d-flex justify-content-end mt-4">
      <material-button
        color="shareguard"
        variant="fill"
        size="sm"
        @click="handleManagePayments"
        >Manage Account
      </material-button>
      <material-button
        v-if="accountTier === 'FREE'"
        class="ms-2"
        color="warning"
        variant="fill"
        size="sm"
        @click="handleUpgradeAccount"
        >Upgrade
      </material-button>
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/ui/MaterialButton.vue";
import MaterialBadge from "@/components/ui/MaterialBadge.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { mapState, mapActions } from "vuex";

export default {
  name: "SubscriptionLimits",
  components: {
    MaterialButton,
    MaterialBadge,
    FontAwesomeIcon
  },
  computed: {
    ...mapState("user", ["isLoading", "stats"]),
    storage() {
      if (!this.stats?.storage || !this.stats?.subscription?.limits?.storageLimit) {
        return { usedFormatted: '0 MB', limitFormatted: '0 MB', usedPercentage: 0 };
      }
      const limitGB = this.stats.subscription.limits.storageLimit;
      const limitFormatted = `${limitGB} GB`;
      const rawPercentage = (this.stats.storage.used / (limitGB * 1024 * 1024 * 1024)) * 100;
      
      // Custom percentage rounding logic
      let displayPercentage;
      if (rawPercentage >= 99 && rawPercentage < 100) {
        displayPercentage = 99;
      } else {
        displayPercentage = Math.min(100, Math.ceil(rawPercentage));
      }

      return {
        ...this.stats.storage,
        limitFormatted,
        usedPercentage: displayPercentage
      };
    },
    transfers() {
      if (!this.stats?.transfers || !this.stats?.subscription) {
        return { uploads: { count: 0, limit: 0, percentage: 0 }, downloads: { count: 0, limit: 0, percentage: 0 } };
      }

      const uploadLimit = this.stats.subscription.limits.monthlyUploadLimit;
      const downloadLimit = this.stats.subscription.limits.monthlyDownloadLimit;
      
      return {
        uploads: {
          ...this.stats.transfers.uploads,
          limit: uploadLimit,
          percentage: Math.min(100, (this.stats.transfers.uploads.count / uploadLimit) * 100)
        },
        downloads: {
          ...this.stats.transfers.downloads,
          limit: downloadLimit,
          percentage: Math.min(100, (this.stats.transfers.downloads.count / downloadLimit) * 100)
        }
      };
    },
    contacts() {
      if (!this.stats?.contacts || !this.stats?.subscription) {
        return { count: 0, limit: 0, percentage: 0 };
      }
      const limit = this.stats.subscription.limits.maxContacts;
      return {
        ...this.stats.contacts,
        limit,
        percentage: Math.min(100, (this.stats.contacts.count / limit) * 100)
      };
    },
    sharing() {
      if (!this.stats?.sharing || !this.stats?.subscription) {
        return { count: 0, limit: 0, percentage: 0 };
      }
      const limit = this.stats.subscription.limits.maxShares;
      return {
        ...this.stats.sharing,
        limit,
        percentage: Math.min(100, (this.stats.sharing.count / limit) * 100)
      };
    },
    accountTier() {
      if (this.isLoading || !this.stats?.subscription) return 'FREE';
      return this.stats.subscription.tier === 'Free' ? 'FREE' : 'PAID';
    }
  },
  mounted() {
    this.populateUserDetails();
  },
  methods: {
    ...mapActions("subscription", ["managePaymentsAction", "upgradeAccountAction"]),
    ...mapActions('user', ['populateUserDetails']),

    async handleManagePayments() {
      await this.managePaymentsAction();
    },
    async handleUpgradeAccount() {
      await this.upgradeAccountAction();
    }
  }
};
</script> 